import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PanelLeft,
  PanelRight,
  MessageSquare,
  Users,
  Settings,
  Plus,
  Sparkles,
  Loader2,
  Play,
  ArrowUpRight,
  CalendarDays,
  Clock,
  Gauge,
  CreditCard,
  AlertCircle,
  X,
  CheckCircle2,
  BellRing,
  Calendar,
} from 'lucide-react';
import { Link } from 'wouter';
import { useAuth, useUser, UserButton } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import ChatInterface, { type ChatMessage } from '../components/ChatInterface';
import InsightsTab from '../components/InsightsTab';
import { BarChart2 } from 'lucide-react';
import { coachPersonas } from '../lib/personas';
import {
  listChatSessions,
  getChatSession,
  createChatSession,
  sendMessageToAI,
  generateMoM,
  saveChatMessage,
  removeSavedMessage,
  getUsageSummary,
  submitSessionFeedback,
  updateNudgeSettings,
  type UsageSummary,
} from '../lib/api';
import type { SessionDetail, SessionSummary } from '../types/session';
import dstyles from './DashboardPage.module.css';

const mockUser = { preferred_coach: 'samuel', first_name: 'User' };

const DashboardPage = () => {
  const { user } = useUser();
  const { getToken, isSignedIn } = useAuth();
  const queryClient = useQueryClient();
  const { data: userProfile } = useCurrentUserProfile();

  const preferredCoach = (user?.publicMetadata?.preferred_coach as string) || mockUser.preferred_coach;

  const personaMap = useMemo(
    () =>
      coachPersonas.reduce<Record<string, (typeof coachPersonas)[number]>>((acc, persona) => {
        acc[persona.id] = persona;
        return acc;
      }, {}),
    [],
  );

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState<'coaches' | 'insights' | 'settings'>('coaches');
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [creationCoaches, setCreationCoaches] = useState<string[]>([]);
  const coachSelectionRef = useRef<HTMLDivElement | null>(null);
  const [highlightCoaches, setHighlightCoaches] = useState(false);
  const prevActiveCoachIdsRef = useRef<string[]>([]);
  
  // Beta notification state
  const [showBetaNotification, setShowBetaNotification] = useState(false);
  const betaTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Scheduling state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulingDate, setSchedulingDate] = useState('');
  const [schedulingTime, setSchedulingTime] = useState('10:00');

  // Beta notification logic
  useEffect(() => {
    const dismissed = localStorage.getItem('hipoAiCoach_betaNotificationDismissed');
    if (!dismissed) {
      const timer = setTimeout(() => {
        setShowBetaNotification(true);
        // Auto-hide after 8 seconds
        betaTimerRef.current = setTimeout(() => {
          handleDismissBetaNotification();
        }, 8000);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (betaTimerRef.current) {
        clearTimeout(betaTimerRef.current);
      }
    };
  }, []);

  const handleDismissBetaNotification = () => {
    localStorage.setItem('hipoAiCoach_betaNotificationDismissed', 'true');
    setShowBetaNotification(false);
    if (betaTimerRef.current) {
      clearTimeout(betaTimerRef.current);
      betaTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (!creationCoaches.length && preferredCoach) {
      setCreationCoaches([preferredCoach]);
    }
  }, [creationCoaches.length, preferredCoach]);

  const {
    data: sessionListData,
    isLoading: isSessionsLoading,
  } = useQuery<SessionSummary[]>({
    queryKey: ['sessions'],
    enabled: isSignedIn,
    queryFn: async () => {
      const token = await getToken();
      return listChatSessions(token!);
    },
  });

  const sessions = sessionListData ?? [];

  const {
    data: usageSummary,
    isLoading: isUsageLoading,
  } = useQuery<UsageSummary>({
    queryKey: ['usage-summary'],
    enabled: isSignedIn,
    queryFn: async () => {
      const token = await getToken();
      if (!token) {
        throw new Error('Missing authentication token');
      }
      return getUsageSummary(token);
    },
    staleTime: 1000 * 60,
  });

  const sortedSessions = useMemo(() => {
    return [...sessions].sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
  }, [sessions]);

  const activeSessionSummary = useMemo(() => {
    if (!selectedSessionId) return null;
    return sortedSessions.find((session) => session.id === selectedSessionId) ?? null;
  }, [sortedSessions, selectedSessionId]);

  useEffect(() => {
    if (!selectedSessionId && sortedSessions.length) {
      console.log('Setting initial selected session to:', sortedSessions[0].id);
      setSelectedSessionId(sortedSessions[0].id);
    }
  }, [sortedSessions, selectedSessionId]);
  
  // Add logging for session selection changes
  useEffect(() => {
    if (selectedSessionId) {
      console.log('Selected session changed to:', selectedSessionId);
      // Force query refetch when session changes
      queryClient.invalidateQueries({ queryKey: ['session', selectedSessionId] });
    }
  }, [selectedSessionId, queryClient]);

  const {
    data: sessionDetailData,
    isLoading: isSessionLoading,
  } = useQuery<SessionDetail | null>({
    queryKey: ['session', selectedSessionId],
    enabled: isSignedIn && Boolean(selectedSessionId),
    queryFn: async () => {
      const token = await getToken();
      return getChatSession(selectedSessionId!, token!);
    },
  });

  const sessionDetail = sessionDetailData ?? null;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [likedMessageIds, setLikedMessageIds] = useState<string[]>([]);
  const [momSummary, setMomSummary] = useState<any>(null);
  const [showLimitReachedOverlay, setShowLimitReachedOverlay] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [limitReachedData, setLimitReachedData] = useState<{
    sessionsUsed: number;
    sessionsPurchased: number;
    currentTier: string;
  } | null>(null);

  useEffect(() => {
    console.log('Session detail updated:', sessionDetail?.id);
    
    if (sessionDetail) {
      console.log('Processing session:', sessionDetail.id, 'Title:', sessionDetail.title);
      console.log('Messages type:', typeof sessionDetail.messages, 'Value:', sessionDetail.messages);
      
      // Handle messages that might be returned as a string or array
      if (Array.isArray(sessionDetail.messages)) {
        console.log('✓ Messages is an array with', sessionDetail.messages.length, 'items');
        console.log('First message sample:', sessionDetail.messages[0]);
        setMessages(sessionDetail.messages);
      } else if (typeof sessionDetail.messages === 'string') {
        console.log(
          'Processing messages string:',
          String(sessionDetail.messages).substring(0, 50) + '...'
        );
        try {
          // Parse messages if they come as a JSON string
          const parsedMessages = JSON.parse(sessionDetail.messages);
          console.log('Parsed message data type:', typeof parsedMessages);
          
          if (Array.isArray(parsedMessages)) {
            console.log('✓ Parsed messages is an array with', parsedMessages.length, 'items');
            setMessages(parsedMessages);
          } else {
            console.warn('⚠ Parsed messages is not an array:', typeof parsedMessages);
            // Try to recover if it's a single message object
            if (typeof parsedMessages === 'object' && parsedMessages !== null) {
              console.log('Attempting to treat as single message object');
              setMessages([parsedMessages]);
            } else {
              setMessages([]);
            }
          }
        } catch (e) {
          console.error('⚠ Failed to parse messages string:', e);
          // If the string is just '[]', set empty array
          if (sessionDetail.messages === '[]') {
            console.log('Empty messages array string detected, using empty array');
            setMessages([]);
          } else {
            console.log('Creating fallback message from string');
            // Create a synthetic message from the string if it's not parseable JSON
            setMessages([{
              id: 'synthetic_' + Date.now(),
              text: String(sessionDetail.messages).substring(0, 500),
              sender: 'system',
              timestamp: new Date().toISOString(),
            }]);
          }
        }
      } else {
        console.warn('⚠ Messages is neither array nor string:', typeof sessionDetail.messages);
        setMessages([]);
      }
      
      console.log('Setting MOM summary:', sessionDetail?.mom_data ? 'exists' : 'null');
      setMomSummary(sessionDetail?.mom_data ?? null);
      
      console.log('Setting liked message IDs:', sessionDetail?.savedMessageIds || []);
      setLikedMessageIds(sessionDetail?.savedMessageIds ?? []);
      
      // Debug the final set messages state
      setTimeout(() => {
        console.log('FINAL MESSAGES STATE:', messages.length, 'messages');
      }, 100);
    } else {
      console.log('No session detail, clearing messages');
      setMessages([]);
      setMomSummary(null);
      setLikedMessageIds([]);
    }
  }, [sessionDetail]);

  const activeCoachIds = useMemo(
    () => Array.from(new Set(creationCoaches)),
    [creationCoaches]
  );

  useEffect(() => {
    const previousCoachIds = prevActiveCoachIdsRef.current;
    const currentCoachIds = activeCoachIds;

    const sortedPrevious = [...previousCoachIds].sort().join(',');
    const sortedCurrent = [...currentCoachIds].sort().join(',');

    if (sortedCurrent && sortedPrevious !== sortedCurrent) {
      let introMessageText = '';
      if (currentCoachIds.length === 1) {
        const coach = personaMap[currentCoachIds[0]];
        if (coach) {
          introMessageText = `Hi, you're now coaching with ${coach.name}, your ${coach.title}. How can I help you?`;
        }
      } else if (currentCoachIds.length > 1) {
        const coachNames = currentCoachIds.map(id => personaMap[id]?.name ?? id);
        const lastCoach = coachNames.pop();
        const coachList = coachNames.join(', ') + ' and ' + lastCoach;
        
        const titles = currentCoachIds.map(id => {
          const title = personaMap[id]?.title ?? '';
          if (title.includes('Leadership')) return 'Leadership';
          if (title.includes('Career')) return 'Career Architecture';
          if (title.includes('Empath')) return 'Empathic Listening';
          return 'Performance';
        });
        const lastTitle = titles.pop();
        const titleList = titles.join(', ') + ' and ' + lastTitle;

        introMessageText = `Hi, you're now coaching with ${coachList}. We will bring together ${titleList} into your coaching discussions.`;
      }

      if (introMessageText) {
        const introMessage: ChatMessage = {
          id: `intro_${Date.now()}`,
          text: introMessageText,
          sender: 'system',
          timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, introMessage]);
      }
    }

    prevActiveCoachIdsRef.current = currentCoachIds;
  }, [activeCoachIds, personaMap]);

  const dashboardMetrics = useMemo(() => {
    const totalSessions = sortedSessions.length;
    const totalMessages = sortedSessions.reduce(
      (acc, session) => acc + (session.message_count ?? 0),
      0
    );
    const coachCounts = new Map<string, number>();
    const coachSet = new Set<string>();
    sortedSessions.forEach((session) => {
      (session.coachTypes ?? []).forEach((coachId) => {
        coachSet.add(coachId);
        coachCounts.set(coachId, (coachCounts.get(coachId) ?? 0) + 1);
      });
    });
    const topCoachIds = Array.from(coachCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([coachId]) => coachId);
    const now = Date.now();
    const sessionsThisWeek = sortedSessions.filter(
      (session) => now - new Date(session.updated_at).getTime() <= 7 * 24 * 60 * 60 * 1000
    ).length;
    const averageMessages = totalSessions ? Math.round(totalMessages / totalSessions) : 0;

    return {
      totalSessions,
      totalMessages,
      sessionsThisWeek,
      averageMessages,
      topCoachIds,
      coachSet,
      lastSession: sortedSessions[0] ?? null,
    };
  }, [sortedSessions]);

  const recommendedCombos = useMemo(() => {
    const combos: string[][] = [];
    const comboKeys = new Set<string>();
    
    // Helper function to add combo only if unique
    const addUniqueCombo = (combo: string[]) => {
      // Sort the combo to ensure consistent key generation
      const sortedCombo = [...combo].sort();
      const key = sortedCombo.join('|');
      
      if (!comboKeys.has(key)) {
        comboKeys.add(key);
        combos.push(combo);
        return true;
      }
      return false;
    };
    
    if (dashboardMetrics.topCoachIds.length >= 2) {
      addUniqueCombo([
        dashboardMetrics.topCoachIds[0],
        dashboardMetrics.topCoachIds[1],
      ]);
    }
    
    if (dashboardMetrics.topCoachIds.length >= 1) {
      const primary = dashboardMetrics.topCoachIds[0];
      if (primary !== 'arjun') {
        addUniqueCombo([primary, 'arjun']);
      }
    }
    
    const fallbacks: string[][] = [
      ['rajesh', 'samuel'],
      ['priya', 'aisha'],
      ['meera', 'arjun'],
    ];
    
    fallbacks.forEach((combo) => {
      addUniqueCombo(combo);
    });
    
    return combos.slice(0, 3);
  }, [dashboardMetrics.topCoachIds]);

  const primaryQuickCombo = recommendedCombos[0] ?? coachPersonas.slice(0, 2).map((persona) => persona.id);

  const handleQuickStart = async (coachIds: string[]) => {
    if (!coachIds.length) return;
    setCreationCoaches(Array.from(new Set(coachIds)));
    const session = await createSessionMutation.mutateAsync(coachIds);
    if (session) {
      setSelectedSessionId(session.id);
      setMessages(session.messages ?? []);
      setMomSummary(session.mom_data ?? null);
      setLikedMessageIds(session.savedMessageIds ?? []);
    }
  };

  // Subscription zone helpers (inline)
  const getSubscriptionZone = (usageSummary: UsageSummary | undefined) => {
    if (!usageSummary) return 'green';
    const percentage = (usageSummary.sessionsUsed / usageSummary.sessionLimit) * 100;
    if (percentage <= 42) return 'green';  // 0-3 sessions of 7
    if (percentage <= 85) return 'yellow'; // 4-6 sessions of 7  
    return 'red'; // 7+ sessions
  };

  const getPlanDisplayName = (usageSummary: UsageSummary | undefined) => {
    if (!usageSummary) return 'Free Trial';
    if (usageSummary.currentTier === 'trial') return 'Free Trial';
    if (usageSummary.currentTier === 'basic') return 'Basic Plan';
    if (usageSummary.currentTier === 'growth') return 'Growth Plan';
    if (usageSummary.currentTier === 'executive') return 'Executive Plan';
    return 'Your Plan';
  };

  const subscriptionZone = getSubscriptionZone(usageSummary);
  const planDisplayName = getPlanDisplayName(usageSummary);

  const metricCards = useMemo(
    () => {
      const cards = [
        {
          label: 'Total Sessions',
          value: dashboardMetrics.totalSessions.toString(),
          delta: dashboardMetrics.sessionsThisWeek ? `${dashboardMetrics.sessionsThisWeek} this week` : undefined,
          icon: CalendarDays,
          accent: 'from-primary/70 via-primary/40 to-primary',
        },
        {
          label: 'Messages Logged',
          value: dashboardMetrics.totalMessages.toString(),
          delta: dashboardMetrics.averageMessages ? `${dashboardMetrics.averageMessages}/session avg` : undefined,
          icon: MessageSquare,
          accent: 'from-blue-500 via-sky-500 to-cyan-500',
        },
        {
          label: 'Active Coaches',
          value: dashboardMetrics.coachSet.size.toString(),
          delta: dashboardMetrics.topCoachIds.length
            ? `Top: ${dashboardMetrics.topCoachIds.map((id) => personaMap[id]?.name ?? id).join(', ')}`
            : undefined,
          icon: Users,
          accent: 'from-purple-500 via-violet-500 to-indigo-500',
        },
        {
          label: 'Latest MoM',
          value: dashboardMetrics.lastSession?.mom_data ? 'Ready' : 'Generating',
          delta: dashboardMetrics.lastSession ? new Date(dashboardMetrics.lastSession.updated_at).toLocaleString() : '—',
          icon: Sparkles,
          accent: 'from-amber-500 via-orange-500 to-orange-600',
        },
      ];

      const sessionsMetric = {
        label: planDisplayName,
        value: usageSummary ? `${usageSummary.sessionsUsed} of ${usageSummary.sessionLimit}` : '—',
        delta: usageSummary
          ? subscriptionZone === 'red' 
            ? 'Sessions completed • Upgrade for unlimited access'
            : subscriptionZone === 'yellow'
            ? `${usageSummary.sessionsRemaining} sessions left • Running low`
            : `${usageSummary.sessionsRemaining} sessions remaining`
          : 'Session quota refreshes automatically',
        icon: subscriptionZone === 'red' ? CreditCard : Gauge,
        accent: subscriptionZone === 'red' 
          ? 'from-amber-500 via-orange-500 to-red-500'
          : subscriptionZone === 'yellow'
          ? 'from-yellow-500 via-amber-500 to-orange-500'
          : 'from-emerald-500 via-teal-500 to-cyan-500',
        loading: isUsageLoading,
      };

      return [sessionsMetric, ...cards];
    },
    [dashboardMetrics, personaMap, usageSummary, isUsageLoading, subscriptionZone, planDisplayName],
  );

  useEffect(() => {
    if (!creationCoaches.length && sessionDetail?.coachTypes?.length) {
      setCreationCoaches(sessionDetail.coachTypes);
    }
  }, [creationCoaches.length, sessionDetail?.coachTypes]);

  useEffect(() => {
    if (!highlightCoaches) return;
    const timer = window.setTimeout(() => setHighlightCoaches(false), 1600);
    return () => window.clearTimeout(timer);
  }, [highlightCoaches]);

  const createSessionMutation = useMutation<SessionDetail | null, Error, string[]>({
    mutationFn: async (coachIds: string[]) => {
      const token = await getToken();
      return createChatSession(coachIds, token!);
    },
    onSuccess: (session) => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      if (session) {
        setSelectedSessionId(session.id);
        setMessages(session.messages ?? []);
        setMomSummary(session.mom_data ?? null);
        setLikedMessageIds(session.savedMessageIds ?? []);
      }
    },
  });

  const sendMessageMutation = useMutation<{ replies: any[]; userMessage: any } | null, Error, { message: string }>({
    mutationFn: async (payload: { message: string }) => {
      if (!selectedSessionId) {
        throw new Error('No session selected');
      }
      const coachIds = creationCoaches;
      if (!coachIds || coachIds.length === 0) {
        throw new Error('Select at least one coach before sending a message');
      }
      const token = await getToken();
      return sendMessageToAI(payload.message, coachIds, selectedSessionId, token!);
    },
    onSuccess: (data) => {
      if (!data) return;
      setMessages((prev) => [...prev, data.userMessage, ...data.replies]);
      queryClient.invalidateQueries({ queryKey: ['session', selectedSessionId] });
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      queryClient.invalidateQueries({ queryKey: ['usage-summary'] });
    },
    onError: (error: any) => {
      // Handle 429 session limit errors
      if (error?.message?.includes('session') || error?.response?.status === 429) {
        try {
          const errorData = JSON.parse(error.message);
          if (errorData.sessionUsage) {
            setLimitReachedData({
              sessionsUsed: errorData.sessionUsage.used,
              sessionsPurchased: errorData.sessionUsage.purchased,
              currentTier: errorData.sessionUsage.tier,
            });
            setShowLimitReachedOverlay(true);
            return;
          }
        } catch (parseError) {
          // If parsing fails, check for session limit keywords
          if (error.message.includes('coaching sessions') || error.message.includes('session limit')) {
            setLimitReachedData({
              sessionsUsed: usageSummary?.sessionsUsed || 0,
              sessionsPurchased: usageSummary?.sessionLimit || 7,
              currentTier: usageSummary?.currentTier || 'trial',
            });
            setShowLimitReachedOverlay(true);
            return;
          }
        }
      }
      console.error('Send message error:', error);
    },
  });

  const momMutation = useMutation<any, Error, void>({
    mutationFn: async () => {
      if (!selectedSessionId) throw new Error('No session selected');
      const token = await getToken();
      return generateMoM(selectedSessionId, token!);
    },
    onSuccess: (summary) => {
      setMomSummary(summary);
      queryClient.invalidateQueries({ queryKey: ['session', selectedSessionId] });
    },
  });

  const likeMutation = useMutation<{ id: string; liked: boolean }, Error, ChatMessage>({
    mutationFn: async (message: ChatMessage) => {
      if (!selectedSessionId) throw new Error('No session selected');
      const token = await getToken();
      const isLiked = likedMessageIds.includes(message.id);
      if (isLiked) {
        await removeSavedMessage(selectedSessionId, message.id, token!);
        return { id: message.id, liked: false } as const;
      }
      const coachType = message.coach ?? (sessionDetail?.coachTypes?.[0] ?? creationCoaches[0] ?? 'samuel');
      await saveChatMessage(selectedSessionId, message.id, message.text, coachType, token!);
      return { id: message.id, liked: true } as const;
    },
    onSuccess: ({ id, liked }) => {
      setLikedMessageIds((prev) => (liked ? [...prev, id] : prev.filter((messageId) => messageId !== id)));
      queryClient.invalidateQueries({ queryKey: ['session', selectedSessionId] });
    },
  });

  const handleEnsureSession = async () => {
    if (selectedSessionId) return selectedSessionId;
    if (!creationCoaches.length) throw new Error('Select at least one coach to start a session');
    const session = await createSessionMutation.mutateAsync(creationCoaches);
    if (!session) {
      throw new Error('Unable to create session');
    }
    return session.id;
  };

  const handleSend = async (text: string) => {
    await handleEnsureSession();
    await sendMessageMutation.mutateAsync({ message: text });
  };

  const handleGenerateMom = () => {
    momMutation.mutate();
  };

  const handleToggleLike = (message: ChatMessage) => {
    likeMutation.mutate(message);
  };

  const handleOpenCoachSelection = () => {
    setActiveTab('coaches');
    if (coachSelectionRef.current) {
      coachSelectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setHighlightCoaches(true);
  };

  const feedbackMutation = useMutation({
    mutationFn: async ({ score, comment }: { score: number; comment: string }) => {
      if (!selectedSessionId) {
        throw new Error('No session selected');
      }
      const token = await getToken();
      return submitSessionFeedback({
        sessionId: selectedSessionId,
        npsScore: score,
        comment,
      }, token!);
    },
    onSuccess: () => {
      if (selectedSessionId) {
        queryClient.invalidateQueries({ queryKey: ['session', selectedSessionId] });
        queryClient.invalidateQueries({ queryKey: ['sessions'] });
      }
    },
  });

  const feedbackErrorMessage = feedbackMutation.isError && feedbackMutation.error instanceof Error
    ? feedbackMutation.error.message
    : null;

  const handleSubmitFeedback = useCallback(
    async (score: number, comment: string) => {
      await feedbackMutation.mutateAsync({ score, comment });
    },
    [feedbackMutation],
  );

  const handleCreateSession = async () => {
    if (creationCoaches.length === 0) return;
    const session = await createSessionMutation.mutateAsync(creationCoaches);
    if (!session) return;
    setSelectedSessionId(session.id);
  };

  const progressWidthClass = (used: number, purchased: number) => {
    if (!purchased) return dstyles.w0;
    const pct = Math.min(Math.round((used / purchased) * 100), 100);
    const step = Math.round(pct / 10) * 10;
    const key = `w${step}` as keyof typeof dstyles;
    return dstyles[key] || dstyles.w0;
  };

  const toggleCoachForCreation = (coachId: string) => {
    setCreationCoaches((prev) => {
      if (prev.includes(coachId)) {
        return prev.filter((id) => id !== coachId);
      }
      return [...prev, coachId];
    });
  };
  
  const updateNudgeMutation = useMutation({
    mutationFn: async (data: { nextSessionAt: string }) => {
      const token = await getToken();
      return updateNudgeSettings(data, token!);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      setShowScheduleModal(false);
    }
  });

  const handleScheduleSession = () => {
    if (!schedulingDate) return;
    const dateTime = `${schedulingDate}T${schedulingTime}:00`;
    updateNudgeMutation.mutate({ nextSessionAt: dateTime });
  };

  const navItems = [
    { id: 'coaches', icon: MessageSquare, label: 'Coaches' },
    { id: 'insights', icon: BarChart2, label: 'Insights' },
    { id: 'settings', icon: Settings, label: 'Settings' },
  ];

  const sessionCoachNames = (coachIds: string[]) =>
    coachIds.map((id) => personaMap[id]?.name ?? id).join(' + ');

  const chatSessionTitle = sessionDetail?.title ?? activeSessionSummary?.title ?? 'Conversation';
  const chatUpdatedAt = sessionDetail?.updated_at ?? activeSessionSummary?.updated_at ?? null;
  const momReady = Boolean(sessionDetail?.mom_data ?? momSummary);

  return (
    <div className="flex min-h-screen bg-slate-50/30 font-sans antialiased">
      {/* Beta Notification Toast */}
      {showBetaNotification && (
        <div className={`fixed top-0 left-0 right-0 z-60 transform transition-transform duration-500 ${
          showBetaNotification ? 'translate-y-0' : '-translate-y-full'
        }`}>
          <div className="bg-primary text-primary-foreground px-4 py-3 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-lg">🚀</span>
                <p className="text-sm font-medium">
                  Beta Release: Thanks for joining us early! Features will evolve as we perfect your coaching experience.
                </p>
              </div>
              <button
                onClick={handleDismissBetaNotification}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-primary-foreground hover:bg-primary-foreground/10 transition-colors sm:h-8 sm:w-8"
                aria-label="Dismiss notification"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      <aside
        className={`relative z-30 hidden flex-col bg-white/80 backdrop-blur-xl shadow-sm transition-all duration-300 lg:flex ${
          isSidebarOpen ? 'lg:w-72' : 'lg:w-20'
        }`}
      >
        <div className={`flex items-center px-6 py-8 ${isSidebarOpen ? 'justify-between' : 'justify-center'}`}>
          {isSidebarOpen && <span className="font-display text-3xl font-semibold text-slate-900 tracking-tight">HiPo</span>}
          <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-3 rounded-xl hover:bg-slate-100/60 text-slate-500 transition-all duration-200">
            {isSidebarOpen ? <PanelLeft className="h-6 w-6" /> : <PanelRight className="h-6 w-6" />}
          </button>
        </div>
        <nav className="flex-1 px-4 py-6 space-y-3">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id as typeof activeTab)}
              className={`flex items-center w-full px-4 py-3.5 rounded-2xl text-sm font-medium transition-all duration-200
                ${isSidebarOpen ? 'justify-start' : 'justify-center'}
                ${activeTab === item.id
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
            >
              <item.icon className="h-5 w-5" strokeWidth={1.5} />
              {isSidebarOpen && <span className="ml-4 font-medium">{item.label}</span>}
            </button>
          ))}
        </nav>
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="flex flex-col gap-6 bg-white/60 backdrop-blur-xl shadow-sm px-6 py-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-5">
            <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 shadow-sm">
              <Play className="h-7 w-7" strokeWidth={1.5} />
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Now progressing</p>
              <div className="flex items-baseline gap-3">
                <h1 className="font-display text-4xl font-semibold text-slate-900 tracking-tight leading-tight">Welcome, {user?.firstName || 'Explorer'}</h1>
                {userProfile?.current_role && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    {userProfile.current_role}
                  </span>
                )}
              </div>
              <p className="text-base text-slate-600 leading-relaxed max-w-2xl">
                {userProfile?.goal_statement || "Curate your coach roster, launch coaching sessions, and stream MoMs on repeat."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {dashboardMetrics.lastSession && (
              <div className="hidden sm:flex flex-col items-end">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">Last session</span>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                  onClick={() => setSelectedSessionId(dashboardMetrics.lastSession?.id ?? null)}
                >
                  {dashboardMetrics.lastSession.title}
                  <ArrowUpRight className="h-4 w-4" />
                </button>
              </div>
            )}
            <Link href="/payments?from=header">
              <a className="hidden sm:inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1.5 text-sm font-semibold text-primary transition hover:bg-primary/20">
                {usageSummary?.currentTier === 'trial' ? 'Trial' : 'Basic'} • {usageSummary ? usageSummary.sessionLimit - usageSummary.sessionsUsed : 7} left
                <CreditCard className="h-3.5 w-3.5" />
              </a>
            </Link>
            <UserButton afterSignOutUrl="/" />
          </div>
        </header>

        {/* Notice Boards */}
        <section className="px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 flex items-center gap-6">
            <div className="h-14 w-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Sessions Completed</p>
              <h3 className="text-3xl font-bold text-slate-900">{userProfile?.sessions_completed ?? 0}</h3>
              <p className="text-xs text-slate-400 mt-1">Keep it up! Consistency is key.</p>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 flex items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className="h-14 w-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <BellRing className="h-7 w-7" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Next Coaching Session</p>
                <h3 className="text-xl font-bold text-slate-900">
                  {userProfile?.next_session_at 
                    ? new Date(userProfile.next_session_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
                    : "Not scheduled"}
                </h3>
                <p className="text-xs text-slate-400 mt-1">
                  {!userProfile?.next_session_at && userProfile?.last_session_at 
                    ? `Recommended: ${new Date(new Date(userProfile.last_session_at).getTime() + 7 * 24 * 3600 * 1000).toLocaleDateString()}`
                    : "Stay on track with regular sessions."}
                </p>
              </div>
            </div>
            <button 
              onClick={() => setShowScheduleModal(true)}
              className="px-4 py-2 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold transition-colors border border-slate-200"
            >
              Schedule
            </button>
          </div>
        </section>

        <main className="flex-1 overflow-y-auto bg-slate-50/30 px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
          <div className="mb-8 flex gap-3 overflow-x-auto pb-2 lg:hidden">
            {navItems.map((item) => {
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id as typeof activeTab)}
                  className={`inline-flex items-center gap-3 rounded-2xl px-5 py-3 text-sm font-medium transition-all duration-200 min-h-[44px] ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'bg-white/80 text-slate-600 hover:bg-white hover:text-slate-900 shadow-sm'
                  }`}
                >
                  <item.icon className="h-4 w-4" strokeWidth={1.5} />
                  {item.label}
                </button>
              );
            })}
          </div>

          <section className="mb-12 space-y-8">
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
              {metricCards.map((metric) => (
                <div
                  key={metric.label}
                  className="relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm p-8 shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${metric.accent} opacity-5`} />
                  <div className="relative flex items-center justify-between">
                    <div className="space-y-3">
                      <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{metric.label}</p>
                      <p className="text-3xl font-semibold text-slate-900 tracking-tight">{metric.value}</p>
                      {metric.delta && <p className="text-sm text-slate-600 leading-relaxed">{metric.delta}</p>}
                    </div>
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 text-slate-600">
                      <metric.icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                  </div>
                  {/* Smart Subscription Widget CTAs - only show when sessions completed */}
                  {metric.label === planDisplayName && subscriptionZone === 'red' && (
                    <div className="mt-4 space-y-2">
                      <Link href="/payments?intent=upgrade&from=dashboard">
                        <button className="w-full rounded-lg bg-primary text-primary-foreground px-3 py-2 text-sm font-medium hover:bg-primary/90 transition-colors min-h-[44px]">
                          Upgrade Plan
                        </button>
                      </Link>
                      <Link href="/payments?intent=topup&from=dashboard">
                        <button className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-slate-50 transition-colors min-h-[44px]">
                          Get 3 More Sessions (₹40)
                        </button>
                      </Link>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="rounded-3xl bg-white/80 backdrop-blur-sm p-8 shadow-sm hover:shadow-md transition-all duration-300">
              <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-3">
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Start coaching session</p>
                  <h3 className="font-display text-2xl font-semibold text-slate-900 tracking-tight leading-tight">Spin up a blended session in one tap</h3>
                  <p className="text-base text-slate-600 leading-relaxed max-w-2xl">We curated combos to get you started on your coaching journey. <Link href="/feedback"><a className="text-primary underline hover:no-underline">Give feedback</a></Link></p>
                </div>
                <button
                  type="button"
                  className="inline-flex items-center gap-3 rounded-2xl bg-primary text-primary-foreground px-6 py-4 text-sm font-medium shadow-sm hover:bg-primary/90 hover:shadow-md transition-all duration-200 min-h-[44px]"
                  onClick={() => handleQuickStart(primaryQuickCombo)}
                >
                  Start with {primaryQuickCombo.map((id) => personaMap[id]?.name ?? id).join(' + ')}
                  <ArrowUpRight className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-8 grid gap-6 md:grid-cols-3">
                {recommendedCombos.map((combo, index) => (
                  <div
                    key={`combo-${index}-${combo.join('-')}`}
                    className="relative overflow-hidden rounded-2xl bg-white/60 backdrop-blur-sm shadow-sm hover:shadow-md transition-all duration-300"
                  >
                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-400 via-transparent to-indigo-400" />
                    <div className="flex items-center gap-4 px-6 py-5">
                      <div className="flex items-center -space-x-3">
                        {combo.map((coachId) => {
                          const coach = personaMap[coachId];
                          if (!coach) return null;
                          return (
                            <div
                              key={coachId}
                              className={`relative h-12 w-12 overflow-hidden rounded-full border-2 border-card bg-gradient-to-br ${coach.color}`}
                            >
                              <img
                                src={`/coach-avatars/${coach.avatar}`}
                                alt={`${coach.name} avatar`}
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                            </div>
                          );
                        })}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground">{combo.map((id) => personaMap[id]?.name ?? id).join(' + ')}</p>
                        <p className="text-xs text-muted-foreground">{combo.map((id) => personaMap[id]?.title ?? '').filter(Boolean).join(' · ') || 'Focused support'}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between border-t border-border/60 bg-card/80 px-4 py-3">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" />
                        ~15 min warm start
                      </div>
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground transition hover:bg-primary/90"
                        onClick={() => handleQuickStart(combo)}
                      >
                        Go live
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {activeTab === 'coaches' && (
            <div className="grid h-full gap-6 lg:grid-cols-[minmax(0,320px)_1fr]">
              <div className="order-1 lg:order-2 lg:sticky lg:top-4">
                <ChatInterface
                  isSessionLoading={isSessionLoading}
                  messages={messages}
                  onSend={handleSend}
                  isSending={sendMessageMutation.isPending}
                  onGenerateMom={handleGenerateMom}
                  isGeneratingMom={momMutation.isPending}
                  momSummary={momSummary}
                  activeCoaches={activeCoachIds}
                  personaMap={personaMap}
                  likedMessageIds={likedMessageIds}
                  onToggleLike={handleToggleLike}
                  sessionTitle={chatSessionTitle}
                  sessionUpdatedAt={chatUpdatedAt}
                  momReady={momReady}
                  onToggleCoach={toggleCoachForCreation}
                  onSubmitFeedback={handleSubmitFeedback}
                  feedback={sessionDetail?.feedback ?? null}
                  isSubmittingFeedback={feedbackMutation.isPending}
                  feedbackError={feedbackErrorMessage}
                  isTyping={isTyping}
                  inputValue={inputValue}
                  onInputChange={setInputValue}
                  onSendMessage={(msg) => sendMessageMutation.mutate({ message: msg })}
                  sessionId={selectedSessionId || ''}
                  onSessionEnd={() => queryClient.invalidateQueries({ queryKey: ['sessions'] })}
                />
              </div>

              <div className="order-2 flex h-full flex-col gap-6 lg:order-1">
                <section
                  ref={coachSelectionRef}
                  className={`rounded-xl border border-border bg-card p-4 shadow-sm transition ${
                    highlightCoaches ? 'ring-2 ring-primary/60 shadow-primary/20' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-display text-base font-semibold text-foreground">Select Your Coaches</h3>
                      <p className="text-xs text-muted-foreground">Select one or more personas before starting a session.</p>
                    </div>
                    <Sparkles className="h-5 w-5 text-primary" />
                  </div>

                  <div className="mt-4 space-y-3">
                    {coachPersonas.map((persona) => {
                      const isSelected = creationCoaches.includes(persona.id);
                      return (
                        <button
                          key={persona.id}
                          type="button"
                          onClick={() => toggleCoachForCreation(persona.id)}
                          className={`w-full rounded-lg border px-3 py-3 text-left transition hover:shadow-md
                            ${isSelected ? 'border-primary bg-primary/10 shadow-sm' : 'border-border bg-background'}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`relative h-12 w-12 shrink-0 overflow-hidden rounded-full ring-2 ${isSelected ? 'ring-primary/70' : 'ring-transparent'} bg-gradient-to-r ${persona.color}`}>
                              <img
                                src={`/coach-avatars/${persona.avatar}`}
                                alt={`${persona.name} avatar`}
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                              {!isSelected && <div className="absolute inset-0 rounded-full bg-black/10" />}
                            </div>
                            <div className="flex-1 space-y-1">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="block font-display text-sm font-semibold text-foreground">{persona.name}</span>
                                  <span className="text-xs text-muted-foreground">{persona.title}</span>
                                </div>
                                <span className={`text-xs font-semibold uppercase tracking-wide ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}>
                                  {isSelected ? 'Selected' : 'Tap to add'}
                                </span>
                              </div>
                              <p className="line-clamp-2 text-xs text-muted-foreground/90">{persona.bio}</p>
                              <div className="flex flex-wrap gap-1 pt-1">
                                {persona.highlightTags?.slice(0, 3).map((tag) => (
                                  <span
                                    key={tag}
                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${isSelected ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={handleCreateSession}
                    disabled={creationCoaches.length === 0 || createSessionMutation.isPending}
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {createSessionMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    Start New Session
                  </button>
                </section>

                <section className="flex-1 overflow-hidden rounded-xl border border-border bg-card p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-base font-semibold text-foreground">Recent Sessions</h3>
                    {isSessionsLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                  </div>
                  <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
                    {sortedSessions.length === 0 && !isSessionsLoading ? (
                      <div className="w-full rounded-lg border border-dashed border-border bg-muted/40 p-4 text-center text-sm text-muted-foreground">
                        No sessions yet. Select your coaches and start a session to begin.
                      </div>
                    ) : (
                      sortedSessions.map((session: SessionSummary) => {
                        const isActive = session.id === selectedSessionId;
                        const primaryCoach = (session.coachTypes ?? [])[0];
                        const coach = primaryCoach ? personaMap[primaryCoach] : undefined;
                        return (
                          <button
                            key={session.id}
                            onClick={() => setSelectedSessionId(session.id)}
                            className={`min-w-[240px] max-w-[260px] flex-shrink-0 rounded-xl border px-4 py-4 text-left transition hover:-translate-y-1 hover:shadow-lg
                              ${isActive ? 'border-primary bg-primary/5' : 'border-border bg-background/95'}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`relative h-12 w-12 overflow-hidden rounded-full bg-gradient-to-br ${coach?.color ?? 'from-slate-500 to-slate-700'}`}>
                                {coach ? (
                                  <img src={`/coach-avatars/${coach.avatar}`} alt={`${coach.name} avatar`} className="h-full w-full object-cover" loading="lazy" />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center text-sm font-semibold text-card-foreground">{session.title[0]}</div>
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-foreground">{session.title}</p>
                                <p className="text-xs text-muted-foreground">{sessionCoachNames(session.coachTypes || [])}</p>
                              </div>
                            </div>
                            <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                              <span>{new Date(session.updated_at).toLocaleString()}</span>
                              <span>{session.message_count ?? 0} msgs</span>
                            </div>
                            {session.mom_data && (
                              <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-1 text-[10px] font-semibold text-primary">
                                <Sparkles className="h-3 w-3" /> MoM ready
                              </div>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </section>
              </div>
            </div>
          )}

          {activeTab === 'insights' && <InsightsTab />}
          {activeTab === 'settings' && (
            <div className="rounded-xl border border-border bg-card p-6 text-center text-muted-foreground">
              Settings will be available shortly.
            </div>
          )}
        </main>
      </div>

      {/* Limit Reached Overlay */}
      {showLimitReachedOverlay && limitReachedData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Session Limit Reached</h3>
              </div>
              <button
                onClick={() => setShowLimitReachedOverlay(false)}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label="Close overlay"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="rounded-lg bg-gray-50 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Sessions Used</span>
                  <span className="text-sm font-bold text-gray-900">
                    {limitReachedData.sessionsUsed} / {limitReachedData.sessionsPurchased}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`bg-amber-500 h-2 rounded-full ${progressWidthClass(limitReachedData.sessionsUsed, limitReachedData.sessionsPurchased)}`}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">Current tier: {limitReachedData.currentTier}</p>
              </div>

              <div className="text-center">
                <p className="text-gray-600 mb-4">
                  You've used all your coaching sessions for this billing period. Upgrade to continue your growth journey.
                </p>
                
                <div className="space-y-3">
                  <Link href="/payments">
                    <button 
                      className="w-full flex items-center justify-center gap-2 bg-royal-blue text-white px-4 py-3 rounded-lg font-medium hover:bg-royal-blue/90 transition"
                      onClick={() => setShowLimitReachedOverlay(false)}
                    >
                      <CreditCard className="h-4 w-4" />
                      Upgrade Plan
                    </button>
                  </Link>
                  
                  <Link href="/payments?intent=topup&from=overlay">
                    <button 
                      className="w-full flex items-center justify-center gap-2 border border-gray-300 bg-white px-4 py-3 rounded-lg font-medium hover:bg-gray-50 transition"
                      onClick={() => setShowLimitReachedOverlay(false)}
                    >
                      <Plus className="h-4 w-4" />
                      Get 3 More Sessions (₹40)
                    </button>
                  </Link>
                  
                  <button 
                    className="w-full text-gray-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition"
                    onClick={() => setShowLimitReachedOverlay(false)}
                  >
                    Maybe Later
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden border border-slate-100">
            <div className="p-8">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                    <Calendar className="h-5 w-5" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900">Schedule Session</h2>
                </div>
                <button 
                  onClick={() => setShowScheduleModal(false)}
                  className="p-2 hover:bg-slate-50 rounded-lg text-slate-400 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Select Date</label>
                  <input 
                    type="date" 
                    value={schedulingDate}
                    onChange={(e) => setSchedulingDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Select Time</label>
                  <input 
                    type="time" 
                    value={schedulingTime}
                    onChange={(e) => setSchedulingTime(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  />
                </div>
              </div>

              <div className="mt-10 flex flex-col gap-3">
                <button 
                  onClick={handleScheduleSession}
                  disabled={!schedulingDate || updateNudgeMutation.isPending}
                  className="w-full py-4 bg-primary text-white rounded-2xl font-bold text-lg shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-3"
                >
                  {updateNudgeMutation.isPending ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Confirm Schedule'
                  )}
                </button>
                <p className="text-center text-xs text-slate-400">
                  We'll send you an email nudge before your session.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
